import type { DraftImage } from "./draft";

export const REWRITE_TYPE = "按建议重写" as const;

export const REVIEW_TARGETS = ["标题", "正文", "封面", "标签"] as const;

const STORAGE_KEY = "mediadesk.revisions";

export interface RevisionImage {
  url: string;
  caption: string;
}

export interface DraftRevision {
  id: string;
  topicId: string;
  version: number;
  title: string;
  body: string;
  tags: string[];
  images: RevisionImage[];
  evaluation: string;
  suggestion: string;
  targets: string[];
  styleVersionId: string | null;
  taskId: string | null;
  kind: "reviewed" | "submitted";
  createdAt: string;
}

export interface DraftView {
  title: string;
  body: string;
  tags: string[];
  images: RevisionImage[];
}

export interface RewriteBrief {
  version: number;
  evaluation: string;
  suggestion: string;
  targets: string[];
  styleVersionId: string | null;
  source: DraftView;
}

export function imageTarget(index: number): string {
  return `第 ${index} 张`;
}

export function submittedImageLine(count: number): string | null {
  if (count > 1) return null;
  return `助理只提交了 ${count} 张图`;
}

export function versionLabel(version: number): string {
  return `第 ${version} 版`;
}

export function rewritingLabel(version: number): string {
  return `重写中·第 ${version} 版`;
}

export function nextVersion(revisions: readonly Pick<DraftRevision, "version">[]): number {
  return revisions.reduce((max, item) => Math.max(max, item.version), 0) + 1;
}

export function rewritingVersion(
  revisions: readonly Pick<DraftRevision, "version">[],
  topicStatus: string,
  tasks: readonly { type: string; topicId?: string | null; status: string }[],
  topicId: string,
): number | null {
  if (topicStatus !== "制作中") return null;
  const active = tasks.some(
    (task) => task.type === REWRITE_TYPE && task.topicId === topicId && (task.status === "排队" || task.status === "进行中"),
  );
  if (!active) return null;
  return nextVersion(revisions);
}

export function rewriteDirection(producedVersion: number, suggestion: string): string {
  const lead = `按建议重写，第 ${producedVersion} 版`;
  const extra = suggestion.trim();
  if (!extra) return lead;
  return `${lead}。${extra}`.slice(0, 400);
}

export function normalizeTargets(values: readonly string[], imageCount: number): string[] {
  const next: string[] = [];
  for (const value of values) {
    const label = value.trim();
    if ((REVIEW_TARGETS as readonly string[]).includes(label)) {
      if (!next.includes(label)) next.push(label);
      continue;
    }
    const match = /^第\s*(\d+)\s*张$/.exec(label);
    const index = match ? Number(match[1]) : 0;
    if (!match || index < 1 || index > Math.max(imageCount, 30)) continue;
    const target = imageTarget(index);
    if (!next.includes(target)) next.push(target);
  }
  return next;
}

export function buildReviewedRevision(
  input: {
    topicId: string;
    title: string;
    body: string;
    tags: string[];
    images: RevisionImage[];
    evaluation: string;
    suggestion: string;
    targets: string[];
    styleVersionId: string | null;
    existing: readonly Pick<DraftRevision, "version">[];
  },
  now: string,
  id: string,
  taskId: string,
): { revision: DraftRevision } | { error: string } {
  const suggestion = input.suggestion.trim();
  if (!suggestion) return { error: "先写一句修改建议。" };
  if (suggestion.length > 2000) return { error: "修改建议太长了，2000 字以内。" };
  const images = input.images
    .filter((image) => image.url.trim())
    .slice(0, 30)
    .map((image) => ({ url: image.url.trim().slice(0, 2000), caption: image.caption.trim().slice(0, 200) }));
  return {
    revision: {
      id,
      topicId: input.topicId,
      version: nextVersion(input.existing),
      title: input.title.trim().slice(0, 200),
      body: input.body.trim().slice(0, 20000),
      tags: input.tags.map((tag) => tag.trim()).filter(Boolean).slice(0, 30),
      images,
      evaluation: input.evaluation.trim().slice(0, 2000),
      suggestion,
      targets: normalizeTargets(input.targets, images.length),
      styleVersionId: input.styleVersionId,
      taskId,
      kind: "reviewed",
      createdAt: now,
    },
  };
}

export function buildSubmittedRevision(
  input: { topicId: string; title: string; body: string; tags: string[]; images: RevisionImage[]; taskId: string; version: number },
  now: string,
  id: string,
): DraftRevision {
  return {
    id,
    topicId: input.topicId,
    version: input.version,
    title: input.title.trim().slice(0, 200),
    body: input.body.trim().slice(0, 20000),
    tags: input.tags.map((tag) => tag.trim()).filter(Boolean).slice(0, 30),
    images: input.images.filter((image) => image.url.trim()).slice(0, 30),
    evaluation: "",
    suggestion: "",
    targets: [],
    styleVersionId: null,
    taskId: input.taskId,
    kind: "submitted",
    createdAt: now,
  };
}

export function agentRewriteBrief(revision: DraftRevision): RewriteBrief {
  return {
    version: revision.kind === "reviewed" ? revision.version + 1 : revision.version,
    evaluation: revision.evaluation,
    suggestion: revision.suggestion,
    targets: revision.targets,
    styleVersionId: revision.styleVersionId,
    source: {
      title: revision.title,
      body: revision.body,
      tags: revision.tags,
      images: revision.images,
    },
  };
}

export function notionRevisionLine(revision: Pick<DraftRevision, "evaluation" | "suggestion" | "targets">): string {
  const where = revision.targets.length ? `针对${revision.targets.join("、")}。` : "";
  const evaluation = revision.evaluation.trim() ? `评价：${revision.evaluation.trim()} ` : "";
  return `${where}${evaluation}${revision.suggestion.trim()}`.trim().slice(0, 500);
}

export function versionChoices(
  revisions: readonly DraftRevision[],
  live: DraftView | null,
): Array<DraftView & { version: number }> {
  const saved = [...revisions]
    .sort((left, right) => left.version - right.version || left.createdAt.localeCompare(right.createdAt))
    .map((revision) => ({
      version: revision.version,
      title: revision.title,
      body: revision.body,
      tags: revision.tags,
      images: revision.images,
    }));
  if (!live) return saved;
  if (saved.some((item) => sameView(item, live))) return saved;
  const version = saved.length ? nextVersion(saved) : 1;
  return [...saved, { version, ...live }];
}

export function slidesFor(images: readonly RevisionImage[]): DraftImage[] {
  return images.filter((image) => image.url.trim()).map((image, index) => ({
    url: image.url,
    caption: image.caption,
    cover: index === 0,
  }));
}

function sameView(left: DraftView, right: DraftView): boolean {
  return (
    left.title.trim() === right.title.trim() &&
    left.body.trim() === right.body.trim() &&
    left.tags.join("\n") === right.tags.join("\n") &&
    left.images.map((image) => image.url).join("\n") === right.images.map((image) => image.url).join("\n")
  );
}

function browserStore(): { getItem(key: string): string | null; setItem(key: string, value: string): void } | null {
  const host = globalThis as { localStorage?: { getItem(key: string): string | null; setItem(key: string, value: string): void } };
  return host.localStorage ?? null;
}

export function readRevisions(topicId?: string): DraftRevision[] {
  try {
    const raw = browserStore()?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const rows = parsed.flatMap((item) => {
      const revision = coerceRevision(item);
      return revision ? [revision] : [];
    });
    return topicId ? rows.filter((item) => item.topicId === topicId) : rows;
  } catch {
    return [];
  }
}

export function rememberRevision(revision: DraftRevision): void {
  const all = readRevisions().filter((item) => item.id !== revision.id);
  all.push(revision);
  try {
    browserStore()?.setItem(STORAGE_KEY, JSON.stringify(all.slice(-200)));
  } catch {
    /* private mode */
  }
}

function coerceRevision(value: unknown): DraftRevision | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<DraftRevision>;
  if (typeof raw.id !== "string" || typeof raw.topicId !== "string" || typeof raw.version !== "number") return null;
  if (raw.kind !== "reviewed" && raw.kind !== "submitted") return null;
  return {
    id: raw.id,
    topicId: raw.topicId,
    version: raw.version,
    title: typeof raw.title === "string" ? raw.title : "",
    body: typeof raw.body === "string" ? raw.body : "",
    tags: Array.isArray(raw.tags) ? raw.tags.filter((item): item is string => typeof item === "string") : [],
    images: Array.isArray(raw.images)
      ? raw.images.flatMap((item) => {
          if (!item || typeof item !== "object") return [];
          const image = item as Partial<RevisionImage>;
          return typeof image.url === "string" && image.url ? [{ url: image.url, caption: typeof image.caption === "string" ? image.caption : "" }] : [];
        })
      : [],
    evaluation: typeof raw.evaluation === "string" ? raw.evaluation : "",
    suggestion: typeof raw.suggestion === "string" ? raw.suggestion : "",
    targets: Array.isArray(raw.targets) ? raw.targets.filter((item): item is string => typeof item === "string") : [],
    styleVersionId: typeof raw.styleVersionId === "string" ? raw.styleVersionId : null,
    taskId: typeof raw.taskId === "string" ? raw.taskId : null,
    kind: raw.kind,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : "",
  };
}
